import type { Dose, Inputs, Recommendation } from '../data/types';
import { DOSE_ORDER } from '../data/types';

/**
 * Weighted score reflecting the user's capacity to sustain dynamic intensity.
 * Higher score → denser dose.
 */
export function score(i: Inputs): number {
  let s = 0;
  s += i.lifeFullness * 1.0;
  s += i.energy * 1.2;
  s += i.supportSystems * 0.8;
  s += i.lifeStability * 1.5; // stability is heavily weighted
  s += i.curiosityVsCommitment * 1.0;
  s +=
    i.experience === 'ongoing-dynamic' ? 5 : i.experience === 'some-scenes' ? 2 : 0;
  s +=
    i.partnerStatus === 'kinky-partner'
      ? 4
      : i.partnerStatus === 'vanilla-partner'
      ? -1
      : 0;
  s += Math.min(i.hoursPerWeek / 4, 10);
  s += i.riskTolerance * 0.5;
  return s;
}

const THRESHOLDS = { bedroom: 15, relationship: 25, lifestyle: 38 } as const;

function rawDose(s: number): Dose {
  if (s < THRESHOLDS.bedroom) return 'bedroom';
  if (s < THRESHOLDS.relationship) return 'relationship';
  if (s < THRESHOLDS.lifestyle) return 'lifestyle';
  return 'tpe';
}

function distanceToNeighbor(s: number, dose: Dose): number {
  // Approx distance to the nearest threshold (above or below) for confidence.
  switch (dose) {
    case 'bedroom':
      return Math.max(0, THRESHOLDS.bedroom - s);
    case 'relationship':
      return Math.min(s - THRESHOLDS.bedroom, THRESHOLDS.relationship - s);
    case 'lifestyle':
      return Math.min(s - THRESHOLDS.relationship, THRESHOLDS.lifestyle - s);
    case 'tpe':
      return Math.max(0, s - THRESHOLDS.lifestyle);
  }
}

export function recommend(i: Inputs): Recommendation {
  const s = score(i);
  let dose = rawDose(s);
  // Honor desired minimum floor
  if (DOSE_ORDER.indexOf(dose) < DOSE_ORDER.indexOf(i.desiredMinDose)) {
    dose = i.desiredMinDose;
  }
  const distance = distanceToNeighbor(s, dose);
  const confidence = Math.min(1, distance / 6);

  const warnings: string[] = [];

  if (dose !== 'bedroom' && i.lifeStability <= 2) {
    warnings.push(
      'your life stability is currently low — even a lighter dose can quickly become a burden when the rest of life is shifting. consider waiting until things settle, or holding to bedroom-only for now.'
    );
  }
  if (dose === 'tpe' && i.experience === 'none') {
    warnings.push(
      'tpe is rarely a good starting point. the surface area is too wide and the recovery from missteps is hard. consider a relationship-style dynamic first, even if your aspiration is tpe.'
    );
  }
  if (dose === 'tpe' && i.supportSystems <= 2) {
    warnings.push(
      'tpe benefits significantly from external support — community, friends in the know, therapy. consider strengthening those connections before going this deep.'
    );
  }
  if (i.partnerStatus === 'vanilla-partner' && dose !== 'bedroom') {
    warnings.push(
      'a vanilla partner generally means a bedroom-scope dynamic unless your relationship is explicitly set up to support more. honor where they are.'
    );
  }
  if (i.partnerStatus === 'solo' && dose !== 'bedroom') {
    warnings.push(
      "without a current partner, you can prepare for any dose — but the lived practice will depend on whom you meet and what you build together. treat this as aspiration, not certainty."
    );
  }
  if (i.energy <= 2 && dose !== 'bedroom') {
    warnings.push(
      "your reported energy is low. a denser dose costs energy you may not have right now. give yourself permission to choose less."
    );
  }

  return { dose, score: s, confidence, warnings };
}

const ENERGY_LABEL: Record<number, string> = {
  1: 'depleted',
  2: 'low',
  3: 'sufficient',
  4: 'good',
  5: 'abundant',
};
const FULLNESS_LABEL: Record<number, string> = {
  1: 'mostly empty',
  2: 'lightly occupied',
  3: 'reasonably full',
  4: 'busy',
  5: 'overflowing',
};
const SUPPORT_LABEL: Record<number, string> = {
  1: 'minimal',
  2: 'thin',
  3: 'moderate',
  4: 'solid',
  5: 'rich',
};
const STABILITY_LABEL: Record<number, string> = {
  1: 'volatile',
  2: 'shaky',
  3: 'workable',
  4: 'stable',
  5: 'very stable',
};

export function reasoning(i: Inputs, r: Recommendation): string[] {
  const paragraphs: string[] = [];

  paragraphs.push(
    `you described your life as ${FULLNESS_LABEL[i.lifeFullness]} and your energy as ${ENERGY_LABEL[i.energy]}. your sense of stability landed at "${STABILITY_LABEL[i.lifeStability]}", with ${SUPPORT_LABEL[i.supportSystems]} support around you. you have about ${i.hoursPerWeek} hour${i.hoursPerWeek === 1 ? '' : 's'} a week to give this.`
  );

  const dynamicShape =
    i.partnerStatus === 'kinky-partner'
      ? 'you have a partner already moving in this direction — the most important variable for higher doses, and you have it'
      : i.partnerStatus === 'vanilla-partner'
      ? 'your partner is vanilla — this is a real constraint, and the most honest doses live around what you can do together'
      : i.partnerStatus === 'dating'
      ? 'you are dating — your dose depends as much on whom you meet as on what you want'
      : 'you are solo — this read is about the dose you could sustain if the right partner arrived';
  paragraphs.push(
    `${dynamicShape}. with ${
      i.experience === 'ongoing-dynamic'
        ? 'an ongoing dynamic already shaping your time'
        : i.experience === 'some-scenes'
        ? 'some scene experience to draw on'
        : 'no prior practice yet'
    }, the calibrator lands at ${r.dose}.`
  );

  if (r.warnings.length === 0) {
    paragraphs.push(
      `what this means in practice: ${doseInPractice(r.dose)}`
    );
  } else {
    paragraphs.push(
      `what this means in practice: ${doseInPractice(r.dose)} — but read the warnings below carefully; the recommendation honors your stated minimum and intensity preferences but does not override the constraints your inputs revealed.`
    );
  }

  return paragraphs;
}

function doseInPractice(d: Dose): string {
  switch (d) {
    case 'bedroom':
      return 'plan scenes deliberately, with negotiation each time. keep the rest of life loose. learn what your body actually wants before opening up to more.';
    case 'relationship':
      return "weave a few daily rituals — a greeting, a posture, a permission, a check-in. don't overbuild; the gentlest version is often the most durable.";
    case 'lifestyle':
      return 'choose two or three domains to shape (sleep, attire, attention) and protocol them with care. let the others remain unstructured. lifestyle gets heavier fast — pace yourself.';
    case 'tpe':
      return 'this is the most demanding dose. for either person, the daily texture is the dynamic. real tpe is rarer in the wild than the fantasy suggests; build community, keep external eyes on the relationship, and revisit the agreement often.';
  }
}
